import type { OrderList } from '@gta/api-client'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { useCallback } from 'react'

import { PaginationBar } from '@/components/console/pagination-bar'
import { ErrorState } from '@/components/console/states'
import { PageContainer } from '@/components/layout/page-container'
import { apiErrorMessage, useOrdersPage } from '@/features/orders/api'
import { OrdersFilterBar } from '@/features/orders/components/orders-filter-bar'
import { OrdersTable } from '@/features/orders/components/orders-table'
import {
  ORDERS_PAGE_SIZE,
  ORDERS_SEARCH_DEFAULTS,
  applyOrdersSearchPatch,
  hasOrdersFilter,
  ordersSearchSchema,
  type OrdersSearch,
} from '@/features/orders/search-params'

/** 稳定引用:没有数据时别每次 render 都造新数组(会让表格白白重算行模型)。 */
const NO_ROWS: OrderList[] = []

export const Route = createFileRoute('/_authed/orders/')({
  // 筛选 / 分页全部进 URL:冷加载 /orders?status=confirmed&page=2 能完整还原。
  // 等于默认值的参数由 stripSearchParams 摘掉(A4 全站统一),裸 /orders 就是干净首屏。
  validateSearch: ordersSearchSchema,
  search: {
    middlewares: [stripSearchParams(ORDERS_SEARCH_DEFAULTS)],
  },
  component: OrdersListPage,
})

function OrdersListPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const query = useOrdersPage(search)

  const patchSearch = useCallback(
    (patch: Partial<OrdersSearch>) => {
      // 筛选变更用 replace:一次连续输入不该在历史里堆几十条记录。
      void navigate({ search: (prev) => applyOrdersSearchPatch(prev, patch), replace: true })
    },
    [navigate],
  )

  const resetSearch = useCallback(() => {
    void navigate({ search: {}, replace: true })
  }, [navigate])

  const changePage = useCallback(
    (page: number) => {
      // 翻页用 push:后退回上一页是用户的合理预期。
      void navigate({ search: (prev) => applyOrdersSearchPatch(prev, { page }) })
    },
    [navigate],
  )

  const page = search.page
  const filtered = hasOrdersFilter(search)
  const total = query.data?.count ?? 0
  const rows = query.data?.results ?? NO_ROWS

  return (
    <PageContainer
      title="订单管理"
      breadcrumbs={[{ label: '订单管理' }]}
      description="按生命周期 / 出行状态、关键词与签约日期区间检索订单;筛选与分页状态全部保存在 URL。"
    >
      <div className="flex flex-col gap-3">
        <OrdersFilterBar value={search} onPatch={patchSearch} onReset={resetSearch} />

        {query.isError ? (
          <ErrorState
            title="订单列表加载失败"
            message={apiErrorMessage(query.error)}
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : (
          <div className="console-card overflow-hidden">
            <OrdersTable
              data={rows}
              isLoading={query.isLoading}
              isRefreshing={query.isFetching && !query.isLoading}
              emptyTitle={filtered ? '没有符合筛选条件的订单' : '暂无订单'}
              emptyDescription={filtered ? '试试放宽状态或日期区间,或清空关键词。' : undefined}
            />
            <PaginationBar page={page} pageSize={ORDERS_PAGE_SIZE} total={total} onPageChange={changePage} />
          </div>
        )}
      </div>
    </PageContainer>
  )
}
